import React from 'react';

import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FormControlComponent } from 'design/formControl';
import { PressControlComponent } from 'design/pressControl';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { INPUT_PASSWORD_MAX_LENGTH, SECOND_MS } from 'utils/constants';
import { useServices } from 'hooks/useServices';
import { MarginComponent } from 'design/margin';
import { CodeInputLayoutComponent } from 'design/codeInputLayout';
import { CharInputs } from 'containers/CharInputs';
import { SpinnerComponent } from 'design/spinner';
import { GridContainerComponent } from 'design/grid';
import { Promocode } from 'types/state/promocode';
import { ReactComponent as CopyIcon } from 'svg/copy.svg';
import { writeToClipboard } from 'utils/clipboardUtils';
import { setCustomTimeout } from 'utils/setCustomTimeout';
import { stringIsEmtyOrDigits } from 'utils/stringUtils';
import { AuthFormBodyLayoutComponent } from 'design/authFormBodyLayout/authFormBodyLayoutComponents';

type Props = {};

type ProcessName = 'AUTHORIZATION' | 'PROMOCODE_REGISTRATION';

type PasswordViolation = 'Missing' | 'Invalid';

const promoCodeRawValue = ['', '', '', '', '', ''];

export const PromocodeRegistrationManagerContent = (props: Props) => {
  const [processName, setProcessName] = React.useState<null | ProcessName>(
    null
  );

  const services = useServices();

  const intl = useIntlDictionary();

  const { promoCodeRegistrationManager: ownDictionary } = intl;

  const [password, setPassword] = React.useState('');

  const [passwordViolations, setPasswordViolations] = React.useState<
    PasswordViolation[]
  >([]);

  const [promocode, setPromocode] = React.useState<null | Promocode>(null);

  const [authorized, setAuthorized] = React.useState(false);

  const [hasCopiedTooltip, setHasCopiedTooltip] = React.useState(false);

  const [
    promocodeGenerationFailed,
    setPromocodeGenerationFailed,
  ] = React.useState(false);

  const { injectPromise } = useAsyncBoundary();

  const handlePasswordChange = React.useCallback((nextValue) => {
    if (stringIsEmtyOrDigits(nextValue)) {
      setPassword(nextValue);
      setPasswordViolations([]);
    }
  }, []);

  const authorize = React.useCallback(() => {
    const rawPassword = password.trim();

    setPasswordViolations([]);

    if (rawPassword.length > 0) {
      setProcessName('AUTHORIZATION');

      const promise = services
        .authorizePromoCodeRegistration(password)
        .then(() => {
          setAuthorized(true);
        })
        .catch((error) => {
          setPasswordViolations(['Invalid']);
        })
        .finally(() => {
          setProcessName(null);
        });

      injectPromise(promise, { shouldShowFallback: false });
    } else {
      setPasswordViolations(['Missing']);
    }
  }, [injectPromise, password, services]);

  const generatePromoCode = React.useCallback(() => {
    setPromocodeGenerationFailed(false);
    setProcessName('PROMOCODE_REGISTRATION');

    const promise = services
      .generatePromoCode()
      .then((promocode) => {
        setPromocode(promocode);
      })
      .catch((error) => {
        setPromocode(null);
        setPromocodeGenerationFailed(true);
      })
      .finally(() => {
        setProcessName(null);
      });

    injectPromise(promise, { shouldShowFallback: false });
  }, [injectPromise, services]);

  const submit = React.useCallback(() => {
    if (!authorized) {
      authorize();
    } else {
      generatePromoCode();
    }
  }, [authorize, authorized, generatePromoCode]);

  const copyPromocodeValueToClipboard = React.useCallback(() => {
    if (promocode) {
      writeToClipboard(promocode.value).then(() => {
        setHasCopiedTooltip(true);
      });
    }
  }, [promocode]);

  React.useEffect(() => {
    if (hasCopiedTooltip) {
      return setCustomTimeout(() => {
        setHasCopiedTooltip(false);
      }, SECOND_MS * 1.5);
    }
  }, [hasCopiedTooltip]);

  React.useEffect(() => {
    if (authorized) {
      generatePromoCode();
    }
  }, [authorized, generatePromoCode]);

  const passwordIsInvalid = passwordViolations.length > 0;

  const promoCodeValue = promocode
    ? promocode.value.split('').filter((char) => char !== ' ')
    : promoCodeRawValue;

  const passwordHint = React.useMemo(() => {
    for (let violation of passwordViolations) {
      switch (violation) {
        case 'Missing':
          return intl.inputValidationRequired;
        case 'Invalid':
          return intl.inputValidationWrongPassword;
        default:
          return undefined;
      }
    }
  }, [
    intl.inputValidationRequired,
    intl.inputValidationWrongPassword,
    passwordViolations,
  ]);

  const invalidCharIndexes = React.useMemo<number[]>(() => {
    if (promocodeGenerationFailed) {
      return promoCodeValue.map((char, index) => index);
    }

    return [];
  }, [promoCodeValue, promocodeGenerationFailed]);

  return (
    <FormControlComponent
      inline={authorized}
      shouldFocusFirstInvalidInput={passwordIsInvalid}
      onSubmit={submit}
    >
      {authorized ? (
        <>
          {promocodeGenerationFailed && (
            <MarginComponent bottom={20}>
              <TextComponent color="critical">
                {ownDictionary.promoCodeRegistrationFailedText}
              </TextComponent>
            </MarginComponent>
          )}
          <CodeInputLayoutComponent
            theme="promoCode"
            inputs={
              <CharInputs
                name="promoCode"
                chars={promoCodeValue}
                onCharsChange={() => {}}
                invalidCharIndexes={invalidCharIndexes}
                readOnly
              />
            }
            indicator={
              processName === 'PROMOCODE_REGISTRATION' && (
                <TextComponent color="accent">
                  <SpinnerComponent />
                </TextComponent>
              )
            }
          />
          <MarginComponent top={20}>
            <GridContainerComponent primaryAlign="center">
              <TextComponent color="secondary">
                {hasCopiedTooltip ? ownDictionary.copiedTooltipText : ' '}
              </TextComponent>
            </GridContainerComponent>
          </MarginComponent>
          <MarginComponent top={20}>
            <GridContainerComponent gap={35} axis="x" secondaryAlign="start">
              <PressControlComponent
                disabled={processName === 'AUTHORIZATION'}
                theme="primary"
                type="submit"
                hasSpinner={processName === 'AUTHORIZATION'}
              >
                {ownDictionary.submitPromoCodeActionText}
              </PressControlComponent>
              <PressControlComponent
                disabled={processName === 'AUTHORIZATION'}
                theme="primary"
                type="button"
                onPress={copyPromocodeValueToClipboard}
                narrow
              >
                <CopyIcon />
              </PressControlComponent>
            </GridContainerComponent>
          </MarginComponent>
        </>
      ) : (
        <AuthFormBodyLayoutComponent>
          <FloatingLabelComponent
            lifted={!!password}
            invalid={passwordIsInvalid}
            titleText={
              <TextComponent color="secondary" size="m">
                {intl.yourPasswordInputPlaceholder}
              </TextComponent>
            }
            hintText={
              <TextComponent color="critical" size="s">
                {passwordHint}
              </TextComponent>
            }
          >
            <GridContainerComponent>
              <TextInputControlComponent
                name="password"
                type="password"
                value={password}
                inputMode="numeric"
                invalid={passwordIsInvalid}
                maxLength={INPUT_PASSWORD_MAX_LENGTH}
                onValueChange={handlePasswordChange}
                autoFocus={!authorized}
              />
            </GridContainerComponent>
          </FloatingLabelComponent>
          <MarginComponent top={48}>
            <PressControlComponent
              disabled={processName === 'AUTHORIZATION'}
              theme="primary"
              type="submit"
              hasSpinner={processName === 'AUTHORIZATION' || authorized}
            >
              {ownDictionary.submitPasswordActionText}
            </PressControlComponent>
          </MarginComponent>
        </AuthFormBodyLayoutComponent>
      )}
    </FormControlComponent>
  );
};
